import { useState } from "react";

import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";

import ScribeGuideEmptyState from "features/medical-scribe/getting-started/scribe-guide-empty-state.component";
import type { ScribeGuideOpenedFrom } from "features/medical-scribe/getting-started/scribe-onboarding-telemetry";
import {
  AMBIENT_SCRIBE_BACKEND_STATUS_PAUSED,
  AMBIENT_SCRIBE_BACKEND_STATUS_PROCESSING,
  AMBIENT_SCRIBE_BACKEND_STATUS_RECORDING
} from "features/medical-scribe/medical-scribe-constants";
import type { AmbientScribe } from "services/types/ambient-scribe.type";

import SelectMedicalScribeField from "./select-medical-scribe-field.component";

// With the paused feature on, recordings that are not finished yet are listed too, so they can
// be opened for editing.
const PAUSED_FEATURE_EXTRA_STATUSES = [
  AMBIENT_SCRIBE_BACKEND_STATUS_PAUSED,
  AMBIENT_SCRIBE_BACKEND_STATUS_PROCESSING,
  AMBIENT_SCRIBE_BACKEND_STATUS_RECORDING
];

type InsertScribeListPanelProps = {
  patientId: number;
  /** Hidden, not unmounted, while the dialog is collapsed, so the search survives. */
  isCollapsed: boolean;
  sx: SxProps<Theme>;
  selected: AmbientScribe | null;
  onSelect: (value: AmbientScribe | null) => void;
  onSelectedScribeSync: (value: AmbientScribe | null) => void;
  onEdit: (scribe: AmbientScribe) => void;
  isPausedFeatureEnabled: boolean;
  isGettingStartedOn: boolean;
  openGuide: (openedFrom: ScribeGuideOpenedFrom) => void;
  onHasRecordingsChange: (hasRecordings: boolean) => void;
};

/** The left panel: the patient's recordings to pick from. */
export default function InsertScribeListPanel({
  patientId,
  isCollapsed,
  sx,
  selected,
  onSelect,
  onSelectedScribeSync,
  onEdit,
  isPausedFeatureEnabled,
  isGettingStartedOn,
  openGuide,
  onHasRecordingsChange
}: InsertScribeListPanelProps) {
  const [search, setSearch] = useState("");

  if (isCollapsed) {
    return null;
  }

  return (
    <Box sx={sx}>
      <SelectMedicalScribeField
        patientId={patientId}
        search={search}
        setSearch={setSearch}
        selected={selected}
        onSelect={onSelect}
        onSelectedScribeSync={onSelectedScribeSync}
        emptyState={
          isGettingStartedOn ? (
            <ScribeGuideEmptyState
              variant="patient"
              onOpenGuide={() => openGuide("insert_scribe")}
            />
          ) : undefined
        }
        onHasRecordingsChange={
          isGettingStartedOn ? onHasRecordingsChange : undefined
        }
        onEdit={isPausedFeatureEnabled ? onEdit : undefined}
        extraStatuses={
          isPausedFeatureEnabled ? PAUSED_FEATURE_EXTRA_STATUSES : undefined
        }
      />
    </Box>
  );
}
